import { useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button } from '@wistia/vhs';
import { Box, Icon, Modal, SegmentedControl, SegmentedControlItem } from '@wistia/ui';
import { cleanPlayerEmbedOptions } from '~/utilities/cleanPlayerEmbedOptions';
import type { MediaLocalization, TimeCodedTranscript } from '~/graphql';
import type { Nilable } from '~/utilities/type-guards';
import {
  isFunction,
  isNonEmptyArray,
  isNonEmptyRecord,
  isNonEmptyString,
} from '~/utilities/type-guards';
import { trackStructEvent } from '~/utilities/snowplow';
import { ThumbnailTab } from './ThumbnailTab';
import { TranscriptTab } from './TranscriptTab';
import { AudioMediaTab } from './AudioMediaTab';
import { AudioTab } from './AudioTab';
import {
  useAnonymousMediaDownloadablesQuery,
  useMediaDownloadablesQuery,
} from './__gql/MediaDownloadables.gql.gen';
import { VideoTab } from './VideoTab';

const dateStringToTimestamp = (dateString: string | undefined) => {
  return new Date(dateString ?? 0).getTime();
};

const dedupe = (files: DownloadableMediaFile[]) => {
  return (
    files
      .sort(
        (date1, date2) =>
          dateStringToTimestamp(date2.createdAt) - dateStringToTimestamp(date1.createdAt),
      )
      // TODO: When Media Platform has a fix for the bug where derivatives are being duplicated, we should remove this filter operation.
      .filter(
        (file, index, array) =>
          array.findIndex((arrayFile) => arrayFile.displayName === file.displayName) === index,
      )
  );
};

const videoTab = { label: 'Video', value: 'video', iconType: 'player' };
const audioTab = { label: 'Audio', value: 'audio', iconType: 'volume-up' };
const thumbnailTab = { label: 'Thumbnail', value: 'thumbnail', iconType: 'thumbnail' };
const transcript = { label: 'Transcript', value: 'transcript', iconType: 'transcript' };

export interface DownloadableMediaFile {
  id: string;
  displayName: string;
  extension?: string;
  width: number;
  height: number;
  length: number;
  formattedFilesize: string;
  downloadUrl: string;
  captionedDownloadUrl: Nilable<string>;
  createdAt?: string;
  mediaHashedId?: string;
}

type Tab = 'audio' | 'thumbnail' | 'transcript' | 'video';

type IconTypes = 'player' | 'thumbnail' | 'transcript' | 'volume-up';

type Props = {
  toggle?: (openModal: () => void) => ReactNode;
  mediaHashedId: string;
  isAnonymousViewer?: boolean;
  trackingLabel?: string;
  startingTab?: Tab;
  onClose?: () => void;
};

export const DownloadModal = ({
  toggle,
  mediaHashedId,
  isAnonymousViewer = false,
  startingTab = 'video',
  onClose,
  trackingLabel,
}: Props): JSX.Element | ReactNode | null => {
  const [selectedTab, setSelectedTab] = useState<Tab>(startingTab);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);

    if (isNonEmptyString(trackingLabel)) {
      trackStructEvent({
        category: 'link',
        action: 'click',
        label: trackingLabel,
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    onClose?.();
  };

  const toggleComponent = isFunction(toggle) ? (
    toggle(openModal)
  ) : (
    <Button
      onClick={openModal}
      data-testid="download_button"
    >
      Download
    </Button>
  );

  const { data: anonymousData } = useAnonymousMediaDownloadablesQuery({
    variables: { mediaHashedId },
    fetchPolicy: 'no-cache',
    skip: !isModalOpen || !isAnonymousViewer,
    onCompleted: ({ media }) => {
      const isAudio = media?.mediaType === 'Audio';

      if (isAudio) {
        setSelectedTab('audio');
      }

      trackStructEvent({
        category: 'modal',
        action: 'view',
        label: `publish-view-download-modal${isAudio ? '-audio' : ''}`,
        property: 'tab',
        value: isAudio ? 'audio' : 'video',
      });
    },
  });

  const { data } = useMediaDownloadablesQuery({
    variables: { mediaHashedId },
    fetchPolicy: 'no-cache',
    skip: !isModalOpen || isAnonymousViewer,
    onCompleted: ({ media }) => {
      const isAudio = media?.mediaType === 'Audio';

      if (isAudio) {
        setSelectedTab('audio');
      }

      trackStructEvent({
        category: 'modal',
        action: 'view',
        label: `publish-view-download-modal${isAudio ? '-audio' : ''}`,
        property: 'tab',
        value: isAudio ? 'audio' : 'video',
      });
    },
  });

  const mediaData = isAnonymousViewer ? anonymousData?.media : data?.media;
  const { embedOptions: rawEmbedOptions, mediaType } = mediaData ?? {};

  if (!rawEmbedOptions || !isModalOpen) {
    return toggleComponent;
  }

  const embedOptions = cleanPlayerEmbedOptions(rawEmbedOptions);
  const isAudio = mediaType === 'Audio';
  const isVideo = mediaType === 'Video';
  const hasIsos = data?.media?.hasIsos ?? false;
  const defaultIetfLanguageTag = mediaData?.ietfLanguageTag ?? 'eng';
  const downloadableNodes = mediaData?.downloadableMediaFiles?.nodes ?? [];
  const downloadableFiles = dedupe(downloadableNodes as DownloadableMediaFile[]);
  const storyboard = downloadableFiles.find((file) => file.displayName === 'Storyboard') ?? {};
  const thumbnail = downloadableFiles.find((file) => file.displayName === 'Thumbnail Image') ?? {};
  const transcripts = (mediaData?.captionableTranscripts ?? []).filter(
    ({ activeVersion }) => activeVersion > 0,
  );

  const showTranscriptTab = !isAnonymousViewer && isNonEmptyArray(transcripts);
  const showThumbnailTab = isNonEmptyRecord(thumbnail);
  const showAudioTab =
    isVideo &&
    mediaData?.__typename !== 'AnonymousMedia' &&
    Boolean(mediaData?.hasAudioDescriptions);

  const tabs = [
    ...(isVideo ? [videoTab] : []),
    ...(isAudio ? [audioTab] : []),
    ...(showThumbnailTab ? [thumbnailTab] : []),
    ...(showTranscriptTab ? [transcript] : []),
    ...(showAudioTab ? [audioTab] : []),
  ];

  const activeTab = {
    video:
      !isAudio && isNonEmptyRecord(mediaData) ? (
        <VideoTab
          mediaId={mediaData.id}
          mediaHashedId={mediaHashedId}
          defaultIetfLanguageTag={defaultIetfLanguageTag}
          hasIsos={hasIsos}
          downloadableMediaFiles={downloadableFiles}
          localizations={mediaData.localizations as MediaLocalization[]}
          captionableTranscripts={transcripts as TimeCodedTranscript[]}
        />
      ) : null,
    audio: isAudio ? (
      <AudioMediaTab downloadableMediaFiles={downloadableFiles} />
    ) : (
      <AudioTab hashedId={mediaHashedId} />
    ),
    thumbnail: showThumbnailTab ? (
      <ThumbnailTab
        embedOptions={embedOptions}
        thumbnail={thumbnail as unknown as DownloadableMediaFile}
        storyboard={storyboard as DownloadableMediaFile}
      />
    ) : null,
    transcript: isAnonymousViewer ? null : (
      <TranscriptTab
        mediaHashedId={mediaHashedId}
        isAudio={isAudio}
        defaultIetfLanguageTag={defaultIetfLanguageTag}
        transcripts={transcripts as TimeCodedTranscript[]}
      />
    ),
  };

  const changeTab = (tab: Tab) => {
    setSelectedTab(tab);

    trackStructEvent({
      category: 'tab',
      action: 'click',
      label: `publish-switch-tab-download-modal${isAudio ? '-audio' : ''}`,
      property: 'tab',
      value: tab,
    });
  };

  return (
    <div>
      {toggleComponent}

      <Modal
        title="Download"
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        positionVariant="fixed-top"
      >
        <Box
          direction="column"
          gap="space-04"
          style={{ marginTop: '1rem' }}
        >
          {tabs.length > 1 ? (
            <SegmentedControl
              fullWidth={true}
              onSelectedValueChange={changeTab}
              selectedValue={selectedTab}
            >
              {tabs.map(({ label, value, iconType }) => (
                <SegmentedControlItem
                  key={value}
                  icon={<Icon type={iconType as IconTypes} />}
                  label={label}
                  value={value}
                />
              ))}
            </SegmentedControl>
          ) : null}

          {activeTab[selectedTab]}
        </Box>
      </Modal>
    </div>
  );
};
